import React, { useRef, useState } from "react";
import styles from "./CSS/AdminProfile.module.css";
import { useAdminProfile } from "../../../hooks/admin/useAdminProfile";
import Loading from "../../../common/HousGlass Loader/Loading";

const AdminProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [previewProfile, setPreviewProfile] = useState(null);
  const [previewBanner, setPreviewBanner] = useState(null);
  const [localProfileData, setLocalProfileData] = useState(null);

  const profileInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const {
    profile,
    updateProfile,
    updateProfileImage,
    updateBannerImage,
    isLoading,
    error,
  } = useAdminProfile();

  // Initialize local state when profile data is loaded
  React.useEffect(() => {
    if (profile) {
      setLocalProfileData(profile);
    }
  }, [profile]);

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();

      reader.onloadend = async () => {
        if (type === "profile") {
          setPreviewProfile(reader.result);
          await updateProfileImage(file);
        } else if (type === "banner") {
          setPreviewBanner(reader.result);
          await updateBannerImage(file);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error(`Error uploading ${type} image:`, error);
      // Reset preview if upload fails
      if (type === "profile") {
        setPreviewProfile(null);
      } else {
        setPreviewBanner(null);
      }
    }
  };

  const handleBannerClick = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.click();
    }
  };

  const handleProfileClick = () => {
    if (profileInputRef.current) {
      profileInputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateProfile({
        author_name: localProfileData.author_name,
        bio: localProfileData.bio,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  if (isLoading) {
    return (
      <Loading/>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>
          {error.message || "An error occurred while loading the profile"}
        </p>
      </div>
    );
  }

  const showCreateProfile = !profile && !isLoading;

  // Function to get profile image URL with proper fallback
  const getProfileImageUrl = () => {
    if (previewProfile) return previewProfile;
    if (profile?.profile_image) {
      return `${profile.profile_image}`;
    }
    return "/default-profile.jpg"; // Make sure to have this default image in your public folder
  };

  // Function to get banner image URL with proper fallback
  const getBannerImageUrl = () => {
    if (previewBanner) return previewBanner;
    if (profile?.banner_image) {
      return `${profile.banner_image}`;
    }
    return "/default-banner.jpg"; // Make sure to have this default image in your public folder
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.profileContainer}>
        {showCreateProfile ? (
          <div className={styles.createProfileContainer}>
            <h2>Create Your Profile</h2>
            <form onSubmit={handleSubmit} className={styles.editForm}>
              <div className={styles.formGroup}>
                <label htmlFor="author_name">Name</label>
                <input
                  type="text"
                  id="author_name"
                  name="author_name"
                  value={localProfileData?.author_name || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="bio">Bio</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={localProfileData?.bio || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button type="submit" className={styles.createButton}>
                Create Profile
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className={styles.bannerSection}>
              <img
                src={getBannerImageUrl()}
                alt="Profile Banner"
                className={styles.bannerImage}
              />
              {isEditing && (
                <>
                  <input
                    type="file"
                    ref={bannerInputRef}
                    onChange={(e) => handleImageUpload(e, "banner")}
                    accept="image/*"
                    style={{ display: "none" }}
                    id="banner-upload"
                  />
                  <button
                    className={styles.uploadBannerBtn}
                    onClick={handleBannerClick}
                    type="button"
                  >
                    Change Banner
                  </button>
                </>
              )}
            </div>

            <div className={styles.profileContent}>
              <div className={styles.profileImageContainer}>
                <img
                  src={getProfileImageUrl()}
                  alt="Profile"
                  className={styles.profileImage}
                />
                {isEditing && (
                  <>
                    <input
                      type="file"
                      ref={profileInputRef}
                      onChange={(e) => handleImageUpload(e, "profile")}
                      accept="image/*"
                      style={{ display: "none" }}
                      id="profile-upload"
                    />
                    <button
                      className={styles.uploadProfileBtn}
                      onClick={handleProfileClick}
                      type="button"
                    >
                      <span>📷</span>
                    </button>
                  </>
                )}
              </div>

              {!isEditing ? (
                <div className={styles.profileInfo}>
                  <div className={styles.nameVerified}>
                    <h1 className={styles.authorName}>
                      {profile?.author_name}
                    </h1>
                    {profile?.is_verified && (
                      <span className={styles.verifiedBadge}>✓</span>
                    )}
                  </div>
                  <p className={styles.bio}>{profile?.bio}</p>

                  <button
                    className={styles.editButton}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className={styles.editForm}>
                  <div className={styles.formGroup}>
                    <label htmlFor="author_name">Name</label>
                    <input
                      type="text"
                      id="author_name"
                      name="author_name"
                      value={localProfileData?.author_name || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={localProfileData?.bio || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className={styles.formActions}>
                    <button type="submit" className={styles.saveButton}>
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => {
                        setIsEditing(false);
                        setPreviewProfile(null);
                        setPreviewBanner(null);
                        setLocalProfileData(profile);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminProfile;